/**
 * Admin Resource Services
 * Uses Content Manager API endpoints for admin users
 * These services require admin authentication
 */

import { API_CONFIG } from '../config/api.config';
import { createAdminResource, fetchAllPages } from './admin-resource-factory';
import { contentManagerService } from './content-manager.service';
import { getDocumentId, cleanDataForSubmit, convertOptions, convertResponse, transformDebitNoteFromStrapi } from './admin-resource-helpers';

// Re-export helpers for any code that imports them from here
export { getDocumentId, convertOptions, convertResponse, cleanDataForSubmit, transformDebitNoteFromStrapi, FIELD_MAPPINGS, transformFieldNames, reverseTransformFieldNames } from './admin-resource-helpers';

const E = API_CONFIG.CONTENT_MANAGER_ENDPOINTS;

// Standard CRUD services
export const clientsService = createAdminResource(E.CLIENTS);
export const banksService = createAdminResource(E.BANKS);
export const branchesService = createAdminResource(E.BRANCHES);
export const currenciesService = createAdminResource(E.CURRENCIES);
export const ranksService = createAdminResource(E.RANKS);
export const categoriesService = createAdminResource(E.CATEGORIES);
export const pettyCashService = createAdminResource(E.PETTY_CASHES);
export const locationsService = createAdminResource(E.LOCATIONS);
export const inventoryCategoriesService = createAdminResource(E.INVENTORY_CATEGORIES);
export const documentTypesService = createAdminResource(E.DOCUMENT_TYPES);
export const systemUsersService = createAdminResource(E.SYSTEM_USERS);
export const rolesService = createAdminResource(E.ROLES);
export const providersService = createAdminResource(E.PROVIDERS);
export const billsService = createAdminResource(E.BILLS);
export const purchasesService = createAdminResource(E.PURCHASES);
export const purchaseItemsService = createAdminResource(E.PURCHASE_ITEMS);
export const invoicesService = createAdminResource(E.INVOICES);
export const contractsService = createAdminResource(E.CONTRACTS);
export const foasService = createAdminResource(E.FOAS);
export const safesService = createAdminResource(E.SAFES);
export const employeesService = createAdminResource(E.EMPLOYEES);
export const candidatesService = createAdminResource(E.CANDIDATES);
export const carsService = createAdminResource(E.CARS);
export const requisitionsService = createAdminResource(E.REQUISITIONS);
export const deliveryNotesService = createAdminResource(E.DELIVERY_NOTES);
export const paymentOrdersService = createAdminResource(E.PAYMENT_ORDERS);
export const invoiceHiresService = createAdminResource(E.INVOICE_HIRES);
export const invoiceHireItemsService = createAdminResource(E.INVOICE_HIRE_ITEMS);
export const crewEvaluationsService = createAdminResource(E.CREW_EVALUATIONS);
export const deductionsService = createAdminResource(E.DEDUCTIONS);
export const productsService = createAdminResource(E.PRODUCTS);
export const codePrefixesService = createAdminResource(E.CODE_PREFIXES);
export const personnelPermitsService = createAdminResource(E.PERSONNEL_PERMITS);
export const carPermitsService = createAdminResource(E.CAR_PERMITS);
export const permitLettersService = createAdminResource(E.PERMIT_LETTERS);
export const careersService = createAdminResource(E.CAREERS);
export const crewCertificatesService = createAdminResource(E.CREW_CERTIFICATES);
export const crewChangesService = createAdminResource(E.CREW_CHANGES);
export const candidateRatingsService = createAdminResource(E.CANDIDATE_RATINGS);
export const assessmentPointsService = createAdminResource(E.ASSESSMENT_POINTS);
export const websiteHeroSettingsService = createAdminResource(E.WEBSITE_HERO_SETTING);
export const serviceItemsService = createAdminResource(E.SERVICE_ITEMS);
export const fleetVesselsService = createAdminResource(E.FLEET_VESSELS);
export const contactOfficesService = createAdminResource(E.CONTACT_OFFICES);
export const erpSettingsService = createAdminResource(E.ERP_SETTINGS);
export const transactionsService = createAdminResource(E.TRANSACTIONS);
export const employeeDeductionsService = createAdminResource(E.EMPLOYEE_DEDUCTIONS);
export const timesheetsService = createAdminResource(E.TIMESHEETS);

// Debit Notes Service - custom implementation due to nested object transforms
export const debitNotesService = {
  getAll: async (options?: any) => {
    const cmOptions = convertOptions(options);
    const response = options?.pagination?.page !== undefined
      ? await contentManagerService.getAll(E.DEBIT_NOTES, cmOptions)
      : await fetchAllPages(E.DEBIT_NOTES, cmOptions);
    return convertResponse(response, E.DEBIT_NOTES);
  },
  getById: async (id: number, options?: any) => {
    const response: any = await contentManagerService.getById(E.DEBIT_NOTES, id, options);
    const data = response.data || response;
    return transformDebitNoteFromStrapi(data);
  },
  create: async (data: any) => {
    const cleanedData = cleanDataForSubmit(data, E.DEBIT_NOTES, false);
    const response: any = await contentManagerService.create(E.DEBIT_NOTES, cleanedData);
    const createdData = response.data || response;
    return transformDebitNoteFromStrapi(createdData);
  },
  update: async (id: number, data: any) => {
    const documentId = getDocumentId(id, data);
    const cleanedData = cleanDataForSubmit(data, E.DEBIT_NOTES, true);
    const response: any = await contentManagerService.update(E.DEBIT_NOTES, documentId, cleanedData);
    const updatedData = response.data || response;
    return transformDebitNoteFromStrapi(updatedData);
  },
  delete: (id: number | string) => {
    const documentId = getDocumentId(id);
    return contentManagerService.delete(E.DEBIT_NOTES, documentId);
  },
};
